import { useState } from 'react';
import { useT } from '@hana/i18n';
import { IconCheck, IconExternalLink, IconX } from '@tabler/icons-react';
import { Button, Drawer, Input, Modal, Segmented, useToast } from '@hana/ui';
import type { Claim, ClaimParty, ContainerFree, CustomsDecl, Deposit, ImportShipment } from './types';
import s from './importModals.module.css';

// Mint-Import · MODAL 2–5 trường (DO · ngày thực tế · phí ngoài biểu · khoản trừ cược · chốt hồ sơ) + NGĂN KÉO "Mở hồ sơ sự cố / claim".
// money = hide: không dựng ô tiền. Chỉ đọc; nút gửi báo toast.

export type ModalKind = 'do' | 'days' | 'fee' | 'ded' | 'close';
export interface ModalReq { kind: ModalKind; id: string }
export interface ModalData {
  imp: readonly ImportShipment[]; dd: readonly ContainerFree[]; lane: readonly CustomsDecl[]; dep: readonly Deposit[]; clm: readonly Claim[];
}
type Tone = 'pri' | 'warn' | 'mute';
const STEP_TONE: Record<Tone, string> = { pri: s.sPri!, warn: s.sWarn!, mute: s.sMute! };
const step = (i: string, k: string, v: string, tone: Tone) => ({ i, k, v, tone });

export function ImportModal({ req, data, onClose }: { req: ModalReq | null; data: ModalData; onClose: () => void }) {
  return req ? <Body key={`${req.kind}:${req.id}`} req={req} data={data} onClose={onClose} /> : null;
}

function Route({ steps }: { steps: ReturnType<typeof step>[] }) {
  const t = useT();
  return (
    <div className={s.route} role="list" aria-label="Luồng duyệt">
      <div className={s.routeK}>{t('imp.flow')}</div>
      {steps.map((x) => (
        <div key={x.i + x.k} className={s.step} role="listitem">
          <span className={`${s.stepI} ${STEP_TONE[x.tone]}`}>{x.i}</span>
          <span className={s.stepK}>{x.k}</span>
          <span className={s.stepV}>{x.v}</span>
        </div>
      ))}
    </div>
  );
}

function Body({ req, data, onClose }: { req: ModalReq; data: ModalData; onClose: () => void }) {
  const t = useT();
  const toast = useToast();
  const [ch, setCh] = useState<'myHANA' | 'Email'>('myHANA');
  const [who, setWho] = useState<'cus' | 'hana'>('cus');
  const done = (msg: string) => { onClose(); toast({ msg: 'Chưa mở ghi ở bản này' }); void msg; };
  const cancel = <Button variant="ghost" icon={<IconX size={16} aria-hidden="true" />} onClick={onClose}>{t('imp.cancel')}</Button>;

  if (req.kind === 'do') {
    const r = data.imp.find((x) => x.id === req.id); if (!r) return null;
    const over = r.doState === 'over' || r.doState === 'overdue';
    const why = r.doState === 'over' ? 'Vượt hạn mức' : r.doState === 'overdue' ? 'Quá hạn công nợ 38 ngày' : 'Chưa thanh toán lô';
    return (
      <Modal open onOpenChange={(o) => !o && onClose()} title="Đề nghị phát DO trước khi thu tiền" description={`${r.id} · ${r.cus} · ${why}`}
        footer={<>{cancel}<Button variant="primary" onClick={() => done('do')}>{t('imp.sendReq')}</Button></>}>
        <div className={s.form}>
          <Input label="Lý do" value="Khách cần lấy hàng trước ngày 25/Oct, cam kết chuyển khoản" readOnly />
          <Input label="Ngày khách cam kết thanh toán" value="24/Oct/2026" readOnly />
          <Route steps={[step('1', 'Sale đề nghị', '[SALE] TUẤN', 'pri'), step('2', over ? 'KTT duyệt — quá hạn / vượt hạn mức' : 'Trưởng KD duyệt — trong hạn mức', over ? '[ACC-CA · Kế toán trưởng] Ghế trống · leo lên BOD-DIR PHILIP' : '[SAL-MGR] DAVID', 'warn'), step('✓', 'Mở khoá nút phát DO', 'ghi log', 'mute')]} />
        </div>
      </Modal>
    );
  }
  if (req.kind === 'days') {
    const r = data.dd.find((x) => x.cont === req.id); if (!r) return null;
    return (
      <Modal open onOpenChange={(o) => !o && onClose()} title="Xác nhận số ngày thực tế" description={`${r.cont} · ${r.job} · ${r.carrier}`}
        footer={<>{cancel}<Button variant="primary" onClick={() => done('days')}>{t('imp.confirm')}</Button></>}>
        <div className={s.form}>
          <Input label="Số ngày lưu thực tế" value={`${r.used} ngày`} readOnly hint="Theo ePort: dỡ 16/Oct → lấy cont 25/Oct" />
        </div>
      </Modal>
    );
  }
  if (req.kind === 'fee') {
    const r = data.lane.find((x) => x.id === req.id); if (!r) return null;
    const fee = r.offTariffNote.split(' · ')[0] ?? '';
    return (
      <Modal open onOpenChange={(o) => !o && onClose()} title="Gửi khách xác nhận phí ngoài biểu" description={`${r.cus} · tờ khai ${r.id}`}
        footer={<>{cancel}<Button variant="primary" onClick={() => done('fee')}>{t('imp.sendCustomer')}</Button></>}>
        <div className={s.form}>
          <div>
            <div className={s.lab}>{t('imp.channel')}</div>
            <Segmented kind="toggle" label="Kênh" value={ch} onChange={setCh} items={[{ key: 'myHANA', label: 'myHANA' }, { key: 'Email', label: 'Email' }]} />
          </div>
          <Input label="Khoản phí" value={fee === '—' || !fee ? 'Rút ruột kiểm hoá' : fee} readOnly />
          <Input label="Ghi chú" value="Số tiền lấy từ biểu phí, chỉ vào debit sau khi khách xác nhận" readOnly />
        </div>
      </Modal>
    );
  }
  if (req.kind === 'ded') {
    const r = data.dep.find((x) => x.cont === req.id); if (!r) return null;
    return (
      <Modal open onOpenChange={(o) => !o && onClose()} title="Quyết định khoản trừ cược" description={`${r.cont} · ${r.carrier}`}
        footer={<>{cancel}<Button variant="primary" onClick={() => done('ded')}>{t('imp.submit')}</Button></>}>
        <div className={s.form}>
          <div>
            <div className={s.lab}>{t('imp.bearer')}</div>
            <Segmented kind="toggle" label="Người chịu" value={who} onChange={setWho} items={[{ key: 'cus', label: 'Tính khách (có EIR/ảnh)' }, { key: 'hana', label: 'HANA chịu' }]} />
          </div>
          <Input label="Bằng chứng" value={r.evidence} readOnly hint={who === 'cus' ? 'Đính kèm EIR và ảnh khi báo khách' : 'Không đủ bằng chứng lỗi khách → ghi chi phí HANA vào Costing'} />
          <Input label="Ghi chú" value="Hãng trừ phí vệ sinh theo biểu CMA CGM" readOnly />
          <Route steps={[step('1', 'CS đề xuất', 'MAI', 'pri'), step('2', 'Trưởng CS duyệt', '[CSD-MGR] giao tạm', 'warn')]} />
        </div>
      </Modal>
    );
  }
  const r = data.clm.find((x) => x.id === req.id); if (!r) return null;
  const big = r.money.amount > 30;
  return (
    <Modal open danger onOpenChange={(o) => !o && onClose()} title={`Chốt hồ sơ ${r.id}?`}
      description={big ? 'Phần HANA chịu vượt ngưỡng duyệt — sẽ chuyển BOD duyệt trước khi chốt.' : 'Sau khi chốt, phần HANA chịu ghi vào Costing của job và không mở lại được.'}
      footer={<><Button variant="ghost" onClick={onClose}>{t('imp.keepFile')}</Button><Button variant={big ? 'primary' : 'danger'} icon={<IconCheck size={16} aria-hidden="true" />} onClick={() => done('close')}>{big ? 'Gửi BOD duyệt' : 'Chốt hồ sơ'}</Button></>}>
      <div className={s.form}>
        <Input label="Bên có lỗi" value={r.party} readOnly />
        <Input label="Ghi vào" value="Costing của job · Kế toán xem số tiền" readOnly />
        {big && <Route steps={[step('1', 'CS đề xuất chốt', 'MAI', 'pri'), step('2', 'BOD duyệt', '[BOD-DIR] PHILIP', 'warn')]} />}
      </div>
    </Modal>
  );
}

const PARTIES: readonly ClaimParty[] = ['Hãng tàu', 'Nhà xe', 'Kho', 'HANA'];
const DEADLINE: Record<ClaimParty, string> = { 'Hãng tàu': '3 ngày từ ngày nhận hàng · 25/Oct/2026', 'Nhà xe': '24 giờ · 23/Oct/2026 10:00', Kho: '24 giờ · 23/Oct/2026 10:00', HANA: 'không áp dụng · ghi vào Costing' };
const FILES: readonly (readonly [string, string])[] = [['Ảnh hiện trường', '+ Thêm ảnh'], ['Biên bản giám định', '+ Tải PDF'], ['EIR / ảnh máy lạnh', '2 ảnh từ HANA Mov']];

/** Ngăn kéo "Mở hồ sơ sự cố / claim" (6+ trường). `from`: mở từ cảnh báo hàng lạnh → điền sẵn mô tả. */
export function ClaimDrawer({ open, from, onClose }: { open: boolean; from?: boolean; onClose: () => void }) {
  const t = useT();
  const toast = useToast();
  const [fault, setFault] = useState<ClaimParty>('Hãng tàu');
  return (
    <Drawer open={open} onOpenChange={(o) => !o && onClose()} title="Mở hồ sơ sự cố / claim" description="CLM-2610-08"
      footer={
        <>
          <span className={s.footNote}>{t('imp.clmTask')}</span>
          <Button variant="ghost" icon={<IconX size={16} aria-hidden="true" />} onClick={onClose}>{t('imp.cancel')}</Button>
          <Button variant="primary" icon={<IconExternalLink size={16} aria-hidden="true" />} onClick={() => { onClose(); toast({ msg: 'Chưa mở ghi ở bản này' }); }}>{t('imp.openFile')}</Button>
        </>
      }>
      <div className={s.grid}>
        <Input label="Job" value="SE260637290" required readOnly />
        <Input label="Khách" value="MKSF" required readOnly />
        <div className={s.span2}><Input label="Mô tả sự cố" required readOnly value={from ? 'Lệch nhiệt độ cont SZLU2019991: −11,2 °C khi nhận, cài −18,0 °C' : 'Mô tả ngắn sự cố, thời điểm, hàng bị ảnh hưởng'} /></div>
        <Input label="Loại tiền" value="VND" readOnly />
        <Input label="Bảo hiểm hàng hoá" value="Không mua" readOnly />
        <Input label="Hạn thông báo tổn thất" value={DEADLINE[fault]} readOnly hint="Tự tính theo loại bên có lỗi" />
        <div className={s.span2}>
          <div className={s.lab}>{t('imp.faultParty')}</div>
          <Segmented kind="toggle" label="Bên có lỗi" value={fault} onChange={setFault} items={PARTIES.map((k) => ({ key: k, label: k }))} />
        </div>
        <div className={`${s.span2} ${s.files}`}>
          {FILES.map(([k, v]) => <button key={k} type="button" className={s.file} onClick={() => toast({ msg: 'Chưa mở ghi ở bản này' })}><b>{k}</b><span>{v}</span></button>)}
        </div>
        <div className={`${s.span2} ${s.note}`}>{fault === 'HANA' ? 'Lỗi HANA: phần bồi thường ghi vào Costing của job; vượt 30 tr cần BOD duyệt.' : `Sau khi mở: gửi thông báo tổn thất cho ${fault.toLowerCase()} trước hạn; số đòi được ghi ngược lại khi chốt.`}</div>
      </div>
    </Drawer>
  );
}
